import React, { useState, useEffect, useRef } from 'react';
import styles from '../../styles/components/Features.module.css';

const Features = () => {
  const [activeFeature, setActiveFeature] = useState('Multi-Currency Wallet');
  const [manualScroll, setManualScroll] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const featureRefs = useRef({});
  const containerRef = useRef(null);

  const features = {
    "Multi-Currency Wallet": {
      content: "Simplify your global transactions by holding and receiving payments in multiple currencies...",
      image: "/Images/feat/1.avif"
    },
    "Order Management": {
      content: "Enhance efficiency with automated order workflows that handle picking, packing, shipping, and returns...",
      image: "/Images/feat/2.png"
    },
    "Inventory and Warehouse Management": {
      content: "Take control of your inventory with real-time tracking across multiple warehouses...",
      image: "/Images/feat/3.avif"
    },
    "Customer Relationship Management (CRM)": {
      content: "Strengthen customer relationships with a unified platform to store, manage, and analyze customer data...",
      image: "/Images/feat/4.avif"
    },
    "Shipment Tracking": {
      content: "Provide transparency to your customers by tracking shipments in real time...",
      image: "/Images/feat/5.png"
    },
    "Customs Brokerage": {
      content: "Navigate the complexities of international trade with ease...",
      image: "/Images/feat/6.avif"
    },
    "Dedicated Account Manager": {
      content: "Receive personalized assistance from a dedicated account manager who understands your business...",
      image: "/Images/feat/7.png"
    },
    "Local Payments": {
      content: "Expand your reach by enabling payments in local currencies...",
      image: "/Images/feat/8.jpg"
    },
    "AI Sales Tools": {
      content: "Empower your business with AI-driven tools for marketing and sales...",
      image: "/Images/feat/9.jpg"
    }
  };

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!manualScroll) {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setActiveFeature(entry.target.getAttribute('data-feature'));
              }
            });
          }
        },
        {
          root: containerRef.current,
          threshold: 0.7, // Increased threshold for more deliberate scrolling
          rootMargin: '-20% 0px -20% 0px' // Added margins to create a more focused detection area
        }
      );

      Object.keys(features).forEach((feature) => {
        if (featureRefs.current[feature]) {
          observer.observe(featureRefs.current[feature]);
        }
      });

      return () => observer.disconnect();
    }
  }, [manualScroll, isMobile]);

  const handleFeatureClick = (feature) => {
    setActiveFeature(feature);
    setManualScroll(true);
    if (featureRefs.current[feature]) {
      featureRefs.current[feature].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    // Increased timeout to prevent quick scrolling
    setTimeout(() => setManualScroll(false), 1500);
  };

  const FeatureCard = ({ feature, content, image }) => (
    <div
      className={`bg-blue-50 p-8 rounded-lg shadow-md mb-12 border-2 rounded-xl ${
        activeFeature === feature ? 'md:border-blue-600' : 'md:border-gray-300'
      }`}
    >
      <h3 className="text-2xl font-semibold mb-6 text-blue-600">{feature}</h3>
      <div className="mb-20 mt-10">
        <img
          src={image}
          alt={feature}
          className="w-full h-48 object-contain rounded-lg"
        />
      </div>
      <p className="text-base text-gray-700 font-small leading-relaxed">{content}</p>
    </div>
  );

  return (
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        <div className="mb-4">
          <h4 className="text-primary font-medium text-center">Features</h4>
        </div>
        <h2 className="text-3xl font-bold text-center mb-8">Our Features</h2>
        <p className="mt-4 text-xl text-gray-600 text-center mb-12">
          Comprehensive tools to streamline your business operations
        </p>

        <div className="rounded-md p-4 shadow-lg border border-gray-300 w-full mx-auto grid grid-cols-1 md:grid-cols-12 gap-8">
          {!isMobile ? (
            <>
              <div
                ref={containerRef}
                className={`${styles['scrollbar-hide']} md:col-span-8 overflow-y-auto max-h-[800px] pr-4 md:pr-8`}
              >
                {Object.entries(features).map(([feature, { content, image }]) => (
                  <div
                    key={feature}
                    ref={(el) => (featureRefs.current[feature] = el)}
                    data-feature={feature}
                    className="min-h-[400px] py-8" // Added minimum height and padding
                  >
                    <FeatureCard feature={feature} content={content} image={image} />
                  </div>
                ))}
              </div>

              <div className="md:col-span-4 relative max-h-[800px] overflow-y-auto pr-4">
                <div className="sticky top-0 space-y-3"> {/* Added space between buttons */}
                  {Object.keys(features).map((feature) => (
                    <button
                      key={feature}
                      onClick={() => handleFeatureClick(feature)}
                      className={`relative pl-8 pr-2 py-3 w-full text-left rounded-lg text-2xl transition-all hover:bg-gray-50 ${
                        activeFeature === feature ? 'bg-gray-50' : ''
                      }`}
                    >
                      <div
                        className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 ${
                          activeFeature === feature
                            ? 'bg-blue-600 border-blue-600'
                            : 'bg-white border-gray-300'
                        }`}
                      />
                      <span
                        className={`text-sm font-medium ${
                          activeFeature === feature ? 'text-blue-600' : 'text-gray-700'
                        }`}
                      >
                        {feature}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="justify-center items-center">
              {Object.entries(features).map(([feature, { content, image }]) => (
                <FeatureCard key={feature} feature={feature} content={content} image={image} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Features;